import * as THREE from 'three';
import { GLTFLoader } from 'three/addons/loaders/GLTFLoader.js';
import { mergeGeometries } from 'three/addons/utils/BufferGeometryUtils.js';

/**
 * Small street clutter from free (CC0) model packs: rubbish bags at the
 * collection points, recycling bins by the vending machines, potted plants,
 * gas cylinders, boxes... See public/models/CREDITS.txt.
 *
 * The world builder only records where things go (PropSpot); once the models
 * have loaded, each kind is drawn as one InstancedMesh per material, so a
 * whole night's clutter costs a couple of dozen draw calls.
 */
export type PropKind =
  | 'trashBags'
  | 'trashBag'
  | 'trashCan'
  | 'boxes'
  | 'papers'
  | 'ac'
  | 'cone'
  | 'plant'
  | 'plantSmall'
  | 'pots'
  | 'propane'
  | 'gasCan'
  | 'bottles'
  | 'crate'
  | 'umbrella';

export type PropSpot = { kind: PropKind; x: number; y: number; z: number; rotY: number; scale?: number; tint?: string };

type Spec = {
  file: string;
  /** Height in metres once loaded (or length, for flat things: see `fit`). */
  size: number;
  fit?: 'height' | 'width';
  /** Recolour materials by name. */
  tints?: Record<string, string>;
  /** Turn the model so its front faces +z (the builder's "outward"). */
  turn?: number;
  shadow?: boolean;
  /** Drop the texture: plain, slightly shiny plastic of this colour. */
  plastic?: string;
};

const SPECS: Record<PropKind, Spec> = {
  // Tokyo's rubbish goes out in translucent white (or the ward's yellow) bags, not black ones.
  trashBags: { file: 'trashbags', size: 0.75, shadow: true, plastic: '#c9ccc4' },
  trashBag: { file: 'trashbag', size: 0.6, shadow: true, plastic: '#c9ccc4' },
  trashCan: { file: 'trashcan', size: 0.95, shadow: true },
  boxes: { file: 'boxes', size: 0.7, shadow: true },
  papers: { file: 'debris', size: 0.5, fit: 'width' },
  ac: { file: 'ac', size: 0.62, shadow: true, tints: { LightGrey: '#bdbbb4', Grey: '#8d8b86', Orange: '#5b5852' } },
  cone: { file: 'cone', size: 0.7, shadow: true, tints: { Orange: '#c23d12', White: '#d9d9d2' } },
  plant: { file: 'plant1', size: 0.9, shadow: true, tints: { Plant_Green: '#3e5a28', Black: '#4a3b30' } },
  plantSmall: { file: 'plant2', size: 0.6, shadow: true, tints: { Plant_Green: '#46622c', Black: '#5c4334' } },
  pots: { file: 'pots', size: 0.55, shadow: true },
  propane: { file: 'propane', size: 1.15, shadow: true, tints: { White: '#9aa0a6', Red: '#8a8f94', Orange: '#4a4d50' } },
  gasCan: { file: 'gascan', size: 0.4, shadow: true },
  bottles: { file: 'bottles', size: 0.4, shadow: true },
  crate: { file: 'crate', size: 0.5, shadow: true },
  umbrella: { file: 'umbrella', size: 0.95, shadow: true },
};

type Part = { geometry: THREE.BufferGeometry; material: THREE.MeshStandardMaterial };

export class PropLibrary {
  private constructor(private readonly parts: Map<PropKind, Part[]>) {}

  /** Load every prop model; a kind whose file fails is just left out. */
  static async load(baseUrl: string): Promise<PropLibrary> {
    const loader = new GLTFLoader();
    const parts = new Map<PropKind, Part[]>();
    const kinds = Object.keys(SPECS) as PropKind[];
    const results = await Promise.allSettled(kinds.map((k) => loader.loadAsync(`${baseUrl}${SPECS[k].file}.glb`)));
    results.forEach((r, i) => {
      if (r.status === 'fulfilled') parts.set(kinds[i], flatten(r.value.scene, SPECS[kinds[i]]));
      else console.warn(`Prop ${kinds[i]} failed to load.`, r.reason);
    });
    if (parts.size === 0) throw new Error('No prop models loaded.');
    return new PropLibrary(parts);
  }

  /** One InstancedMesh per kind and material for these spots. Dispose the group's meshes, not the shared geometry. */
  build(spots: readonly PropSpot[]): THREE.Group {
    const group = new THREE.Group();
    group.name = 'props';
    const byKind = new Map<string, PropSpot[]>();
    for (const s of spots) {
      if (!this.parts.has(s.kind)) continue;
      // Tinted spots get their own batch (one colour per instanced material).
      const key = s.tint ? `${s.kind}|${s.tint}` : s.kind;
      const list = byKind.get(key) ?? [];
      list.push(s);
      byKind.set(key, list);
    }
    const m = new THREE.Matrix4();
    const q = new THREE.Quaternion();
    const p = new THREE.Vector3();
    const sc = new THREE.Vector3();
    const up = new THREE.Vector3(0, 1, 0);
    for (const [key, list] of byKind) {
      const kind = list[0].kind;
      const spec = SPECS[kind];
      const tint = list[0].tint;
      for (const part of this.parts.get(kind)!) {
        let material = part.material;
        // A tint repaints the body; black trim (lids, handles) stays black.
        if (tint && part.material.name !== 'Black') {
          material = part.material.clone();
          material.color.set(tint);
        }
        const mesh = new THREE.InstancedMesh(part.geometry, material, list.length);
        mesh.name = `prop:${key}`;
        list.forEach((s, i) => {
          q.setFromAxisAngle(up, s.rotY);
          mesh.setMatrixAt(i, m.compose(p.set(s.x, s.y, s.z), q, sc.setScalar(s.scale ?? 1)));
        });
        mesh.castShadow = !!spec.shadow;
        mesh.receiveShadow = true;
        mesh.computeBoundingSphere();
        if (material !== part.material) mesh.userData.ownMaterial = true;
        group.add(mesh);
      }
    }
    return group;
  }
}

/** Dispose what build() made: the instance buffers and any tinted material copies. */
export function disposeProps(group: THREE.Group): void {
  for (const o of group.children) {
    const mesh = o as THREE.InstancedMesh;
    if (mesh.userData.ownMaterial) (mesh.material as THREE.Material).dispose();
    mesh.dispose();
  }
}

/**
 * Bake a loaded model into one geometry per material: scaled to size, sitting
 * on y = 0, centred, facing +z.
 */
function flatten(scene: THREE.Object3D, spec: Spec): Part[] {
  scene.updateMatrixWorld(true);
  const box = new THREE.Box3().setFromObject(scene);
  const size = box.getSize(new THREE.Vector3());
  const span = spec.fit === 'width' ? Math.max(size.x, size.z) : size.y;
  const s = spec.size / Math.max(1e-6, span);
  const centre = box.getCenter(new THREE.Vector3());
  const place = new THREE.Matrix4()
    .makeRotationY(spec.turn ?? 0)
    .multiply(new THREE.Matrix4().makeScale(s, s, s))
    .multiply(new THREE.Matrix4().makeTranslation(-centre.x, -box.min.y, -centre.z));
  const byMaterial = new Map<string, { material: THREE.MeshStandardMaterial; geos: THREE.BufferGeometry[] }>();
  scene.traverse((o) => {
    const mesh = o as THREE.Mesh;
    if (!mesh.isMesh) return;
    const src = (Array.isArray(mesh.material) ? mesh.material[0] : mesh.material) as THREE.MeshStandardMaterial;
    const geo = mesh.geometry.clone();
    geo.applyMatrix4(new THREE.Matrix4().multiplyMatrices(place, mesh.matrixWorld));
    // Keep only what every part has, so geometries of one material merge.
    for (const name of Object.keys(geo.attributes)) {
      if (name !== 'position' && name !== 'normal' && name !== 'uv') geo.deleteAttribute(name);
    }
    if (!geo.attributes.uv) {
      geo.setAttribute('uv', new THREE.Float32BufferAttribute(new Float32Array(geo.attributes.position.count * 2), 2));
    }
    if (!geo.index) geo.setIndex([...Array(geo.attributes.position.count).keys()]);
    const entry = byMaterial.get(src.name || src.uuid);
    if (entry) {
      entry.geos.push(geo);
      return;
    }
    const material = src.clone();
    const tint = spec.tints?.[src.name];
    if (tint) material.color.set(tint);
    material.roughness = Math.max(material.roughness, 0.7);
    material.metalness = Math.min(material.metalness, 0.2);
    if (spec.plastic) {
      material.map = null;
      material.color.set(spec.plastic);
      material.roughness = 0.38;
    }
    byMaterial.set(src.name || src.uuid, { material, geos: [geo] });
  });
  const parts: Part[] = [];
  for (const { material, geos } of byMaterial.values()) {
    const geometry = geos.length === 1 ? geos[0] : mergeGeometries(geos, false);
    if (!geometry) continue;
    geometry.computeBoundingSphere();
    parts.push({ geometry, material });
  }
  return parts;
}
